import { compact, sum } from '@quromedical/utils';
import * as Scale from '../scales';
import { news2RiskScale } from '../scales';
import { NEWS2ClinicalRisk, OxygenSaturationScale } from '../types';
const consciousnessScore = {
  alert: 0,
  confusion: 3,
  pain: 3,
  unresponsive: 3,
  verbal: 3
};
/**
 * Represents the `Air or Oxygen` question in the NEWS2 Chart
 */

const oxygenSaturationScaleScore = {
  [OxygenSaturationScale.Scale1]: 0,
  [OxygenSaturationScale.Scale2Air]: 0,
  [OxygenSaturationScale.Scale2Oxygen]: 2
};
const oxygenSaturationScales = {
  [OxygenSaturationScale.Scale1]: Scale.spo2Scale1,
  [OxygenSaturationScale.Scale2Air]: Scale.spo2Scale2Air,
  [OxygenSaturationScale.Scale2Oxygen]: Scale.spo2Scale2Oxygen
};

const flattenScores = scores => {
  const keys = Object.keys(scores);
  const parameterScores = keys.map(key => {
    const parameterValue = scores[key];

    if (typeof parameterValue === 'number') {
      return parameterValue;
    }

    return parameterValue === null || parameterValue === void 0 ? void 0 : parameterValue.value;
  });
  return compact(parameterScores);
};
/**
 * If there are any risks of level 3 then the lowest possible risk level is `low-medium`
 */


const getMinimumAllowedRisk = scores => {
  const parameterScores = flattenScores(scores);
  const hasAnyHigh = parameterScores.some(val => val === 3);
  return hasAnyHigh ? NEWS2ClinicalRisk.LowMedium : NEWS2ClinicalRisk.Low;
};

const comparativeRiskValue = {
  [NEWS2ClinicalRisk.Low]: 1,
  [NEWS2ClinicalRisk.LowMedium]: 2,
  [NEWS2ClinicalRisk.Medium]: 3,
  [NEWS2ClinicalRisk.High]: 4
};

const getMaxRisk = (a, b) => {
  const aComparative = comparativeRiskValue[a];
  const bComparative = comparativeRiskValue[b];
  return aComparative > bComparative ? a : b;
};

const getRisk = (total, scores) => {
  const lowestRisk = getMinimumAllowedRisk(scores);
  const scoreRisk = news2RiskScale(total);
  return getMaxRisk(lowestRisk, scoreRisk);
};

const getScore = scores => {
  const parameterScores = flattenScores(scores);
  return sum(parameterScores);
};

export const calculateNEWS2Score = params => {
  const consciousness = consciousnessScore[params.consciousness];
  const oxygenSaturationScale = oxygenSaturationScaleScore[params.oxygenSaturationScale];
  const oxygenSaturation = oxygenSaturationScales[params.oxygenSaturationScale](params.oxygenSaturation);
  const pulseRate = Scale.pulseRate(params.pulseRate);
  const respirationRate = Scale.respirationRate(params.respirationRate);
  const systolicBloodPressure = Scale.systolicBloodPressure(params.systolicBloodPressure);
  const temperature = Scale.temperature(params.temperature);
  const parameterScores = {
    consciousness,
    oxygenSaturationScale,
    oxygenSaturation,
    pulseRate,
    respirationRate,
    systolicBloodPressure,
    temperature
  };
  const score = getScore(parameterScores);
  const risk = getRisk(score, parameterScores);
  return {
    parameterScores,
    risk,
    score
  };
};
/**
 * Quro medical score. Represents a partial NEWS2 score calculated based on the sum of individual
 * NEWS2 parameter scores added together
 */

export const calculateQuroScore = params => {
  const {
    oxygenSaturation: oxygenSaturationValue,
    systolicBloodPressure: systolicBloodPressureValue
  } = params;
  const consciousness = consciousnessScore[params.consciousness];
  const oxygenSaturationScale = oxygenSaturationScaleScore[params.oxygenSaturationScale];
  const oxygenSaturation = oxygenSaturationValue ? oxygenSaturationScales[params.oxygenSaturationScale](oxygenSaturationValue) : undefined;
  const pulseRate = Scale.pulseRate(params.pulseRate);
  const respirationRate = Scale.respirationRate(params.respirationRate);
  const systolicBloodPressure = systolicBloodPressureValue ? Scale.systolicBloodPressure(systolicBloodPressureValue) : undefined;
  const temperature = Scale.temperature(params.temperature);
  const parameterScores = {
    consciousness,
    oxygenSaturationScale,
    oxygenSaturation,
    pulseRate,
    respirationRate,
    systolicBloodPressure,
    temperature
  };
  const score = getScore(parameterScores);
  const risk = getRisk(score, parameterScores);
  return {
    parameterScores,
    risk,
    score
  };
};
//# sourceMappingURL=news2.js.map